import { Image, Intro, Text, Title } from "components/commons";
import DataTableV2 from "components/commons/data-table/data-table-v2";
import { ErrorCode, PagesKeys, Path } from "enums";
import { useAlert, useApi, useModal, useRouter } from "hooks";
import locale from "localization";
import ProductListFilter from "./product-list-filter";
import useDataTable from "hooks/useDataTable";
import { getProductsApi, deleteProductApi } from "apis";
import { initialFilterState } from "./product-filter.state";
import { mapDataToList, mapFilterToRequest } from "./product-list.mapper";
import { productColumn } from "./product-list-column";
import useAuth from "hooks/useAuth";
import useStations from "hooks/useStations";
import ConfirmModal from "components/modals/confirm-modal/confirm-modal";
import React, { useCallback, useEffect, useState } from "react";
import { IconTrash } from "images";
import styles from "./product-list.module.scss";

const ProductsListModule = () => {
  const router = useRouter();
  const { allStations } = useStations({
    initialRun: true,
  });

  const { hasPermission, stationCodes } = useAuth({ pageKey: PagesKeys.PRODUCTS });
  const { addAlert } = useAlert();

  const [openMenuProductId, setOpenMenuProductId] = useState(null);
  const [deletingProductId, setDeletingProductId] = useState(null);
  const highlightedProductId = openMenuProductId || deletingProductId;

  const deleteConfirmModal = useModal();

  const deleteProduct = useApi({ api: deleteProductApi });

  const handleOnOpenMenu = useCallback((productId, isOpen) => {
    setOpenMenuProductId(isOpen ? productId : null);
  }, []);

  const handleOnDeleteClick = useCallback(
    (product) => {
      setDeletingProductId(product.productId);
      deleteConfirmModal.show({
        primary: {
          text: locale.yes,
        },
        secondary: {
          text: locale.no,
          onClick: () => setDeletingProductId(null),
        },
        productId: product.productId,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deleteConfirmModal],
  );

  const { filter, search, table } = useDataTable({
    api: {
      api: getProductsApi,
      params: {
        page: 1,
        perPage: 10,
        orderBy: "createdAt",
        stationCodes,
      },
      mapper: (param) => {
        return param;
      },
    },
    filter: {
      initialState: initialFilterState(),
      mapper: (fs) => {
        const mappedStationCodes = mapFilterToRequest(fs).stationCodes;
        return {
          ...mapFilterToRequest(fs),
          stationCodes: mappedStationCodes || stationCodes,
        };
      },
    },
    table: {
      key: "products",
      mapper: (products) =>
        mapDataToList({
          products,
          router,
          allStations,
          highlightedProductId,
          onOpenMenu: handleOnOpenMenu,
          onDeleteClick: handleOnDeleteClick,
        }),
      columns: productColumn,
    },
  });

  const handleSubmitDeleteProduct = useCallback(async () => {
    const res = await deleteProduct.request({ id: deleteConfirmModal.productId }, () => {}, {
      reqError: true,
    });

    if (res?.error) {
      deleteConfirmModal.close();
      setDeletingProductId(null);

      const cannotDeleteProductError = {
        [ErrorCode.ProductHasExistingTransaction]: locale.productHasExistingTransactionError,
        [ErrorCode.ProductHasExistingDeliveryLogs]: locale.productHasExistingDeliveryLogsError,
      }[res.error?.data?.errorCode];

      return addAlert({
        title: cannotDeleteProductError ? locale.cannotDeleteProduct : "Something wrong!",
        subtitle: cannotDeleteProductError || res.error?.data?.message,
        type: "error",
        interval: 10000,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteConfirmModal.productId]);

  useEffect(() => {
    if (deleteProduct?.called && !deleteProduct?.error) {
      deleteConfirmModal.close();
      setDeletingProductId(null);
      table?.refetch();
      addAlert({
        title: locale.productDeleted,
        subtitle: locale.successfullyDeletedProduct,
        type: "success",
        interval: 5000,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteProduct?.called, deleteProduct?.error]);

  return (
    <>
      <ConfirmModal
        {...deleteConfirmModal}
        centerActions
        primary={{
          ...deleteConfirmModal?.primary,
          onClick: handleSubmitDeleteProduct,
          loading: deleteProduct?.loading,
        }}
      >
        <div className={styles.deleteProductModalContent}>
          <Image src={IconTrash} style={{ width: "135px" }} />
          <Title style={{ color: "#000", textAlign: "center" }}>{`${locale.deleteProduct}?`}</Title>
          <Text color="black">{locale.confirmDeleteProduct}</Text>
        </div>
      </ConfirmModal>
      <Intro
        title={locale.products}
        actionText={hasPermission(["addAccess"]) && locale.addProduct}
        actionOnClick={() => router.push(Path.AddProduct)}
      />
      <ProductListFilter {...filter} {...search} />
      <DataTableV2 {...table} paginationV2 />
    </>
  );
};

export default ProductsListModule;
